/**
 * Readable text on a fleet's own colors.
 *
 * A fleet picks its brand colors, not the color of the text that sits on
 * them, so the text color is derived: white or dark, whichever has the higher
 * WCAG 2.x contrast ratio against the background.
 *
 * @see https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
 * @see https://www.w3.org/TR/WCAG21/#dfn-contrast-ratio
 */

export type RgbColor = { r: number; g: number; b: number };

/** Light text: white. */
export const LIGHT_TEXT_COLOR = "#ffffff";

/** Dark text: Tailwind slate-900, the theme's foreground. */
export const DARK_TEXT_COLOR = "#0f172a";

const HEX_COLOR = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

/**
 * Parses `#RGB` / `#RRGGBB` (case-insensitive, `#` optional). Returns null for
 * anything else, including named colors and `rgb()` strings.
 */
export function parseHexColor(
  value: string | null | undefined,
): RgbColor | null {
  const match = value?.trim().match(HEX_COLOR);
  if (!match) return null;

  const digits = match[1]!;
  const hex =
    digits.length === 3
      ? digits
          .split("")
          .map((digit) => digit + digit)
          .join("")
      : digits;

  return {
    r: parseInt(hex.slice(0, 2), 16),
    g: parseInt(hex.slice(2, 4), 16),
    b: parseInt(hex.slice(4, 6), 16),
  };
}

const linearize = (channel: number): number => {
  const srgb = channel / 255;
  return srgb <= 0.04045 ? srgb / 12.92 : ((srgb + 0.055) / 1.055) ** 2.4;
};

/** WCAG relative luminance, 0 (black) to 1 (white); null for a non-hex color. */
export function relativeLuminance(color: string): number | null {
  const rgb = parseHexColor(color);
  if (!rgb) return null;

  return (
    0.2126 * linearize(rgb.r) +
    0.7152 * linearize(rgb.g) +
    0.0722 * linearize(rgb.b)
  );
}

/**
 * WCAG contrast ratio between two colors, 1 (none) to 21 (black on white).
 * Symmetric. Null when either color is not a hex color.
 */
export function contrastRatio(a: string, b: string): number | null {
  const first = relativeLuminance(a);
  const second = relativeLuminance(b);
  if (first === null || second === null) return null;

  const lighter = Math.max(first, second);
  const darker = Math.min(first, second);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * The text color to put on `background`: white or dark, whichever contrasts
 * more with it. A missing or non-hex background gets dark text, which is what
 * the default light theme expects.
 */
export function getReadableTextColor(
  background: string | null | undefined,
): string {
  if (!background || !parseHexColor(background)) return DARK_TEXT_COLOR;

  const withLight = contrastRatio(background, LIGHT_TEXT_COLOR)!;
  const withDark = contrastRatio(background, DARK_TEXT_COLOR)!;
  return withLight > withDark ? LIGHT_TEXT_COLOR : DARK_TEXT_COLOR;
}

/**
 * `color` at the given alpha as `rgba()`, e.g. a divider in the text color.
 * Anything that is not a hex color is returned unchanged.
 */
export function withAlpha(color: string, alpha: number): string {
  const rgb = parseHexColor(color);
  if (!rgb) return color;

  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
}

const toHexChannel = (channel: number): string =>
  Math.round(channel).toString(16).padStart(2, "0");

/**
 * The solid color `color` looks like when painted at `alpha` (0..1, clamped)
 * over `backdrop` (white by default), as `#rrggbb`. This is what a
 * semi-transparent background actually shows, so it is the color readable
 * text has to be picked against. Blends in sRGB, the way browsers composite.
 * Returns `color` unchanged when either color is not a hex color.
 */
export function blendOver(
  color: string,
  alpha: number,
  backdrop: string = LIGHT_TEXT_COLOR,
): string {
  const top = parseHexColor(color);
  const bottom = parseHexColor(backdrop);
  if (!top || !bottom) return color;

  const a = Math.min(1, Math.max(0, alpha));
  const mix = (over: number, under: number) => over * a + under * (1 - a);

  return `#${toHexChannel(mix(top.r, bottom.r))}${toHexChannel(
    mix(top.g, bottom.g),
  )}${toHexChannel(mix(top.b, bottom.b))}`;
}
